import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SoftwareCard from '@/components/feature/SoftwareCard';
import Pagination from '@/components/feature/Pagination';
import { categories } from '@/mocks/categories';
import { softwares } from '@/mocks/softwares';
import useDocumentMeta from '@/hooks/useDocumentMeta';

const PAGE_SIZE = 6;

type SortKey = 'latest' | 'downloads' | 'rating';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '按发布时间' },
  { key: 'downloads', label: '按下载量' },
  { key: 'rating', label: '按评分' },
];

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [activeSub, setActiveSub] = useState('');
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  const category = categories.find((c) => c.id === categoryId);

  useDocumentMeta({
    title: category
      ? `${category.name} - 黄金网站软件app免费`
      : '软件分类 - 黄金网站软件app免费',
    description: category
      ? category.description.slice(0, 150)
      : '黄金网站软件app免费分类页，按功能筛选实用的黄金投资软件与应用。',
    keywords: category
      ? `黄金网站软件app免费,${category.name},${category.subCategories.join(',')}`
      : '黄金网站软件app免费,黄金投资软件分类',
  });

  const filtered = useMemo(() => {
    if (!category) return [];
    let list = softwares.filter((s) => s.categoryId === category.id);
    if (activeSub) {
      list = list.filter((s) => s.tags.includes(activeSub));
    }
    if (sort === 'latest') {
      list = [...list].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
    } else if (sort === 'downloads') {
      list = [...list].sort((a, b) => b.downloads - a.downloads);
    } else {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [category, activeSub, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setActiveSub('');
    setSort('latest');
    setPage(1);
  };

  if (!category) {
    return (
      <SiteLayout>
        <Breadcrumb items={[{ label: '软件分类' }]} />
        <div className="rounded-lg border border-background-200 bg-background-50 p-12 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
            未找到该分类
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            该分类可能已调整或不存在，去看看全部软件吧。
          </p>
          <Link
            to="/softwares"
            className="mt-5 inline-flex h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-secondary-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors items-center justify-center"
          >
            浏览全部软件
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Breadcrumb items={[{ label: '软件分类', path: '/softwares' }, { label: category.name }]} />

      <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex items-start gap-4">
          <span className="w-14 h-14 rounded-lg bg-secondary-950 text-primary-400 flex items-center justify-center shrink-0">
            <i className={`${category.icon} text-3xl`} />
          </span>
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              {category.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-3xl">
              {category.description}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => {
              setActiveSub('');
              setPage(1);
            }}
            className={`h-8 px-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
              activeSub === ''
                ? 'bg-secondary-950 text-background-50 font-medium'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {category.subCategories.map((sub) => (
            <button
              key={sub}
              type="button"
              onClick={() => {
                setActiveSub(sub);
                setPage(1);
              }}
              className={`h-8 px-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                activeSub === sub
                  ? 'bg-secondary-950 text-background-50 font-medium'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {sub}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
          <select
            value={sort}
            onChange={(e) => {
              setSort(e.target.value as SortKey);
              setPage(1);
            }}
            className="h-9 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 outline-none focus:border-primary-400 cursor-pointer"
          >
            {sortOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="mt-3 text-sm text-foreground-500">
        共找到 <span className="text-primary-700 font-medium">{filtered.length}</span> 款
        {category.name}
      </p>

      {paged.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {paged.map((s) => (
              <SoftwareCard key={s.id} software={s} variant="grid" />
            ))}
          </div>
          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-12 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-inbox-line text-3xl" />
          </span>
          <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
            暂无相关软件
          </h3>
          <p className="mt-2 text-sm text-foreground-500">
            当前筛选条件下没有匹配的软件，换个条件试试。
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 inline-flex h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-secondary-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors items-center justify-center"
          >
            重置筛选
          </button>
        </div>
      )}
    </SiteLayout>
  );
}